import { useCallback, useMemo, useState } from 'react'

import { ComboBoxItem } from '../types/comboBoxItem'
import { ComboBoxSelectedItem } from '../types/comboBoxSelectedItem'
import { ComboBoxValue } from '../types/comboBoxValue'
import { isMaxLimitReached } from '../utils/isMaxLimitReached'

type HandleSelectedItemCallback = (newItems: ComboBoxSelectedItem[]) => void

// this hook is to be used in the context only
export const useSelectedItems = (maxItems?: number) => {
  const [selectedItems, setSelectedItems] = useState<ComboBoxSelectedItem[]>([])

  const isMaxItemsLimitReached = useMemo(
    () => isMaxLimitReached(selectedItems.length, maxItems),
    [maxItems, selectedItems.length],
  )

  const isItemSelected = useCallback(
    (item: ComboBoxItem | ComboBoxSelectedItem) =>
      selectedItems.some((selectedItem) => item.value.value === selectedItem.value.value),
    [selectedItems],
  )

  const isValueInSelectedItems = useCallback(
    (value: string) => selectedItems.some((selectedItem) => selectedItem.value.value === value),
    [selectedItems],
  )

  const addItems = useCallback(
    (items: ComboBoxSelectedItem[], onAdd?: HandleSelectedItemCallback) => {
      if (isMaxLimitReached(selectedItems.length, maxItems)) {
        return
      }

      const uniqueNotSelectedItems = items.filter(
        (item, index, self) => !isItemSelected(item) && index === self.findIndex(({ id }) => id === item.id),
      )
      const newItems = [...selectedItems, ...uniqueNotSelectedItems]

      if (isMaxLimitReached(newItems.length, maxItems) && typeof maxItems === 'number') {
        newItems.splice(maxItems)
      }

      setSelectedItems(newItems)
      onAdd?.(newItems)
    },
    [isItemSelected, maxItems, selectedItems],
  )

  const addItem = useCallback(
    (item: ComboBoxSelectedItem, onAdd?: HandleSelectedItemCallback) => {
      if (isItemSelected(item) || isMaxLimitReached(selectedItems.length, maxItems)) {
        return
      }

      const newItems = [...selectedItems, item]
      setSelectedItems(newItems)
      onAdd?.(newItems)
    },
    [isItemSelected, maxItems, selectedItems],
  )

  const setSingleItem = useCallback((item: ComboBoxSelectedItem, onAdd?: HandleSelectedItemCallback) => {
    const newItems = [item]
    setSelectedItems(newItems)
    onAdd?.(newItems)
  }, [])

  const removeItem = useCallback(
    (valueToRemove: ComboBoxValue, onRemove?: HandleSelectedItemCallback) => {
      const newItems = [...selectedItems.filter((item) => item.value.value !== valueToRemove)]
      setSelectedItems(newItems)
      onRemove?.(newItems)
    },
    [selectedItems, setSelectedItems],
  )

  return {
    addItem,
    addItems,
    isItemSelected,
    isMaxItemsLimitReached,
    isValueInSelectedItems,
    removeItem,
    selectedItems,
    setSingleItem,
  }
}
